/**
 * Custody-schedule view + upsert. Shows a 14-day grid coloured by owning parent
 * and a form to set/update the rotation (week-week / 2-2-3 / custom).
 */
import { useState } from 'react';
import type { CustodyPattern, CustodySchedule } from '@solandia/core';
import { toIsoDate } from '@solandia/core';
import { useI18n } from '../../i18n';
import { useApi } from '../../api/ApiProvider';
import { useFamily } from '../../family/FamilyContext';
import { useAsync } from '../../hooks/useAsync';
import {
  Badge,
  ErrorState,
  Field,
  Form,
  Spinner,
} from '../../components/ui';
import { buildCustodyWindow } from './custody';

const PATTERNS: CustodyPattern[] = ['week-week', '2-2-3', 'custom'];
const DOW_SK = ['Po', 'Ut', 'St', 'Št', 'Pi', 'So', 'Ne'];

export function CustodyView() {
  const { t } = useI18n();
  const api = useApi();
  const { family, activeChild } = useFamily();
  const childId = activeChild!.id;

  const { data, loading, error, reload } = useAsync<CustodySchedule | null>(
    () => api.getCustodySchedule(family.id, childId),
    [family.id, childId],
  );

  const [editing, setEditing] = useState(false);

  if (loading) return <Spinner />;
  if (error) return <ErrorState message={t.errors.generic} onRetry={reload} />;

  const schedule = data ?? null;
  const window = schedule ? buildCustodyWindow(schedule, new Date(), 14) : [];

  return (
    <div className="stack">
      <div className="row-between">
        <div className="row">
          <h3>{t.custody.title}</h3>
          {schedule ? (
            <Badge tone="primary">{t.custody.patterns[schedule.pattern]}</Badge>
          ) : null}
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setEditing((e) => !e)}
        >
          {schedule ? t.custody.update : t.custody.set}
        </button>
      </div>

      {!schedule && !editing ? (
        <div className="banner banner-info">{t.custody.none}</div>
      ) : null}

      {schedule && !editing ? (
        <>
          <div className="custody-legend">
            <span>
              <span className="legend-swatch" style={{ background: 'var(--sol-primary-tint)' }} />
              {t.custody.parentA}
            </span>
            <span>
              <span className="legend-swatch" style={{ background: 'var(--sol-accent-tint)' }} />
              {t.custody.parentB}
            </span>
          </div>
          <div className="custody-grid">
            {window.map((d, i) => (
              <div
                key={i}
                className={[
                  'custody-day',
                  d.parent === 'A' ? 'parent-a' : 'parent-b',
                  d.isToday ? 'is-today' : '',
                ].join(' ')}
              >
                <span className="dow">{DOW_SK[(d.date.getUTCDay() + 6) % 7]}</span>
                <span className="num">{d.date.getUTCDate()}</span>
              </div>
            ))}
          </div>
          {schedule.pattern === 'custom' ? (
            <p className="text-xs muted">
              Vlastný vzor — pridajte konkrétne odovzdávky cez udalosti v kalendári.
            </p>
          ) : null}
          {schedule.notes ? <p className="text-sm">{schedule.notes}</p> : null}
        </>
      ) : null}

      {editing ? (
        <CustodyForm
          existing={schedule}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CustodyForm({
  existing,
  onCancel,
  onSaved,
}: {
  existing: CustodySchedule | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const { family, activeChild } = useFamily();
  const [pattern, setPattern] = useState<CustodyPattern>(existing?.pattern ?? 'week-week');
  const [startDate, setStartDate] = useState(existing?.startDate ?? toIsoDate(new Date()));
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await api.upsertCustodySchedule(family.id, activeChild!.id, {
        pattern,
        startDate,
        notes: notes.trim() || null,
      });
      onSaved();
    } catch {
      setErr(t.errors.generic);
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <Form onSubmit={submit}>
        {err ? <ErrorState message={err} /> : null}
        <Field label={t.custody.patternLabel}>
          {({ id }) => (
            <select
              id={id}
              className="select"
              value={pattern}
              onChange={(e) => setPattern(e.target.value as CustodyPattern)}
            >
              {PATTERNS.map((p) => (
                <option key={p} value={p}>
                  {t.custody.patterns[p]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label={t.custody.startLabel}>
          {({ id }) => (
            <input
              id={id}
              type="date"
              className="input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          )}
        </Field>
        <Field label={`${t.custody.notesLabel} (${t.common.optional})`}>
          {({ id }) => (
            <textarea
              id={id}
              className="textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          )}
        </Field>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            {t.common.cancel}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t.common.saving : t.common.save}
          </button>
        </div>
      </Form>
    </div>
  );
}
